import React from "react";
import { Helmet } from "react-helmet-async";
import TrustBand from "../../components/TrustBand/TrustBand";
import "./SeoLandingPages.css";

import { SITE_URL } from "../../utils/siteSeo.mjs";
const IOS_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";

export const SEO_LANDING_PAGES = {
  "/firearm-inventory-app": {
    title: "Firearm Inventory App | Collection Records & Range History",
    h1: "A firearm inventory app for organized owners.",
    kicker: "Firearm Inventory App",
    description:
      "Range Pocket helps responsible firearm owners track firearms, ammo, accessories, receipts, photos, maintenance, and range history in one organized app.",
    intro:
      "A firearm inventory should be easy to keep current and clear enough to review. Range Pocket gives owners a structured place to document collection details, ammo, accessories, maintenance, photos, notes, and usage history without turning records into spreadsheet upkeep.",
    primaryCta: "Explore Range Pocket",
    image: "/firearm_list.png",
    useCases: [
      "Firearm make, model, caliber, source, and purchase notes",
      "Accessory, ammo, receipt, and photo organization",
      "Range usage history connected to individual firearms",
    ],
    sections: [
      {
        title: "Track the details that matter",
        body:
          "Record firearm make, model, caliber, purchase details, photos, notes, accessories, and configuration history in a format that is easier to search than a spreadsheet and easier to keep current than paper.",
      },
      {
        title: "Connect inventory to range activity",
        body:
          "Inventory becomes more useful when it connects to real use. Range Pocket links firearms to range visits, round counts, notes, target photos, and training sessions so each item has context over time.",
      },
      {
        title: "Designed around useful review",
        body:
          "Range Pocket is built as a personal record tool, so records are organized around the details owners actually want to track and review later.",
      },
    ],
    faqs: [
      {
        q: "Can Range Pocket replace a firearm inventory spreadsheet?",
        a: "Yes. It is built for structured firearm, ammo, accessory, photo, range visit, and maintenance records without forcing you to maintain rows and columns manually.",
      },
      {
        q: "Where are normal Range Pocket records kept?",
        a: "Range Pocket is built as a personal record tool for everyday firearm inventory records, range history, photos, notes, and maintenance context.",
      },
    ],
  },
  "/range-log-app": {
    title: "Range Log App | Track Range Visits, Notes & Round Counts",
    h1: "A range log app built for real shooting sessions.",
    kicker: "Range Log App",
    description:
      "Range Pocket helps shooters log range visits, firearms used, round counts, target photos, notes, range locations, training plans, and performance history.",
    intro:
      "A good range log should be fast enough to use at the range and detailed enough to help later. Range Pocket keeps range visits, firearms used, round counts, target photos, notes, and training context together so each session becomes part of a useful history.",
    primaryCta: "See Range Pocket",
    image: "/range_log.png",
    useCases: [
      "Range, date, distance, conditions, and session notes",
      "Firearms used, round counts, drills, and target photos",
      "Training context that is easy to review before the next visit",
    ],
    sections: [
      {
        title: "Capture sessions while they are fresh",
        body:
          "Log the range, date, firearms, round counts, notes, target photos, distance, conditions, and drills while the details are still accurate.",
      },
      {
        title: "Build continuity between visits",
        body:
          "Review previous sessions before the next one, see which firearms you used, compare notes, and keep training plans connected to what actually happened on the line.",
      },
      {
        title: "Keep range history useful",
        body:
          "Range logs are most valuable when they are easy to review. Range Pocket keeps visits, notes, firearms, and target photos connected so the next session starts with context.",
      },
    ],
    faqs: [
      {
        q: "What should I record in a range log?",
        a: "At minimum, record date, range, firearms used, round counts, drills, notes, and target photos. Range Pocket gives you a structured place for all of those details.",
      },
      {
        q: "Can I connect a range log to a firearm?",
        a: "Yes. Range Pocket connects visits and logs to firearms so each item can build a meaningful usage history over time.",
      },
    ],
  },
  "/firearm-maintenance-tracker": {
    title: "Firearm Maintenance Tracker | Logs, Round Counts & Reminders",
    h1: "A firearm maintenance tracker connected to real use.",
    kicker: "Firearm Maintenance Tracker",
    description:
      "Range Pocket helps shooters track firearm maintenance, round counts, accessory changes, battery reminders, notes, photos, and range history in one organized app.",
    intro:
      "Maintenance is easier when it is connected to how a firearm is actually used. Range Pocket helps owners track round counts, cleaning notes, accessory changes, battery reminders, photos, and range history so maintenance becomes part of the same record as training and inventory.",
    primaryCta: "Open Range Pocket",
    image: "/content/blog/firearm-maintenance-log-what-to-track/maintenance_plan_screen.png",
    useCases: [
      "Cleaning notes and maintenance history by firearm",
      "Round counts connected to real range activity",
      "Accessory changes, battery reminders, and configuration notes",
    ],
    sections: [
      {
        title: "Use round counts as context",
        body:
          "Round counts become more useful when they connect to specific firearms and range visits. Range Pocket helps owners understand how often each firearm is used and when follow-up maintenance may be needed.",
      },
      {
        title: "Record changes and reminders",
        body:
          "Track cleaning notes, accessory updates, optic or battery reminders, photos, and configuration changes without scattering the information across notes, photos, and spreadsheets.",
      },
      {
        title: "Keep maintenance records with the firearm",
        body:
          "Firearm maintenance logs can include equipment, round counts, and usage details. Range Pocket keeps those records connected to the firearm so they are easier to review before the next session.",
      },
    ],
    faqs: [
      {
        q: "Can Range Pocket track maintenance by firearm?",
        a: "Yes. Maintenance notes, round counts, photos, and related range activity can be organized around individual firearms.",
      },
      {
        q: "Can I track accessory battery changes?",
        a: "Yes. Range Pocket includes accessory-focused tracking, including battery-life reminders for equipment that needs periodic replacement.",
      },
    ],
  },
};

function faqJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

function SeoLandingPage({ page }) {
  const canonical = `${SITE_URL}${page.path}`;
  const imageUrl = `${SITE_URL}${page.image}`;
  return (
    <main className="seo-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(page))}</script>
      </Helmet>

      <section className="seo-hero">
        <div className="seo-hero-copy">
          <div className="seo-kicker">{page.kicker}</div>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
          <div className="seo-cta-row">
            <a className="seo-button seo-button-primary" href="/range-pocket">
              {page.primaryCta}
            </a>
            <a className="seo-button seo-button-secondary" href={IOS_URL}>
              App Store
            </a>
            <a className="seo-button seo-button-secondary" href={ANDROID_URL}>
              Google Play
            </a>
            <a className="seo-button seo-button-secondary" href="/features">
              See features
            </a>
          </div>
          <p className="seo-support-note">
            Questions before downloading? Email <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
          </p>
        </div>
        <div className="seo-hero-media" aria-hidden="true">
          <img src={page.image} alt="" />
        </div>
      </section>

      <section className="seo-use-cases" aria-label={`${page.kicker} use cases`}>
        {page.useCases.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </section>

      <TrustBand className="seo-trust-band" />

      <section className="seo-section-grid" aria-label={`${page.kicker} details`}>
        {page.sections.map((section) => (
          <article className="seo-info-card" key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
          </article>
        ))}
      </section>

      <section className="seo-faq" aria-labelledby={`${page.path.slice(1)}-faq`}>
        <div className="seo-kicker">Common Questions</div>
        <h2 id={`${page.path.slice(1)}-faq`}>{page.kicker} FAQ</h2>
        <div className="seo-faq-list">
          {page.faqs.map((item) => (
            <article key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="seo-related" aria-label="Related resources">
        <a href="/features">Range Pocket features</a>
        <a href="/range-pocket">Range Pocket</a>
        <a href="/faq">FAQ</a>
        <a href="/blog/range-pocket-complete-tool">Everyday shooter workflow</a>
        <a href="/blog/range-time-to-training-habit">Training habits</a>
      </section>
    </main>
  );
}

export const SeoLandingRoutes = Object.entries(SEO_LANDING_PAGES).map(([path, page]) => ({
  path,
  element: <SeoLandingPage page={{ ...page, path }} />,
}));

export default SeoLandingPage;
